import React, {
  useState, useCallback, useMemo, useEffect, useRef,
} from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { actions } from '@/store';
import theme from '@/styles/theme';
import useAlert from '@/hooks/use-alert';
import useSelectorUser from '@/hooks/use-selector-user';
import Message from '@/constant/message';
import { EAuthModalPage } from '@/store/types';
import postCheckConfirmCode from '@/fetches/user/auth/post-check-confirm-code';
import postSendConfirmEmail from '@/fetches/user/auth/post-send-confirm-email';
import postJoinMember from '@/fetches/user/auth/new/post-join-member';
import postNewSocialEmail from '@/fetches/user/auth/post-new-social-email';
import validator from '@/constant/valitator';
import postCheckEmailStatus from '@/fetches/user/auth/post-check-email-status';
import media from '@/styles/media';
import LoadingSpinner from '@/components/pages/user/common/loading-spinner';
import { useCounterDown } from '../common/use-counter-down';
import { Container, ContentDiv, Wrapper } from '../common/new-auth-container';
import { BasicButton, BasicButtonLabel, ButtonDiv } from '../common/new-auth-button';
import { BalloonModal } from '../common/balloon-modal';
import { CodeInput, EmailInput } from '../common/new-auth-input';
import { TextLink, Title } from '../common/new-auth-text';
import MobileLogo from '../common/auth-layout/mobile-logo';

/** 이메일 인증, 새이메일 등록, 비밀번호 재설정 컴포넌트 */
export const AuthEmail = () => {
  const {
    register, email, name, phone, belong, actualName, password, confirmCodeType, token, gotoAuthLogin, pageType,
  } = useSelectorUser('authModal');
  const [id, setId] = useState<string>('');
  const [code, setCode] = useState<string>('');
  const [isId, setIsId] = useState<boolean>(false);
  const [idMsg, setIdMsg] = useState<string>('');
  const [sendComplete, setSendComplete] = useState(false);
  const [isMailModalOpen, setIsMailModalOpen] = useState<boolean>(false);
  const [isTimeModalOpen, setIsTimeModalOpen] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const focusRef = useRef<HTMLInputElement >(null);
  const { remainSeconds, startCountDown, stopCountDown } = useCounterDown();
  const { t, i18n } = useTranslation();
  const { alert } = useAlert();
  const dispatch = useDispatch();

  useEffect(() => {
    if (focusRef.current) {
      focusRef.current.focus();
    }
  }, [focusRef]);

  const title = useMemo(() => {
    switch (confirmCodeType) {
      default:
      case 'email': return Message.이메일인증;
      case 'social': return Message.새이메일등록;
      case 'pwreset': return Message.비밀번호재설정;
    }
  }, [confirmCodeType]);

  const gotoLogin = useCallback(() => {
    dispatch(actions.user.authModal.setEmail(''));
    if (gotoAuthLogin) { gotoAuthLogin(); }
    // dispatch(actions.user.authModal.setCurrentPage(EAuthModalPage.INPUT_EMAIL));
  }, [dispatch, gotoAuthLogin]);

  const goBack = useCallback(() => {
    dispatch(actions.user.authModal.setEmail(''));
    dispatch(actions.user.authModal.setCurrentPage(EAuthModalPage.SIGN_UP));
  }, [dispatch]);

  const onChangeIdInput = useCallback((event:React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    const check = t(validator.checkEmail(value));
    setId(value);
    setIdMsg(check);

    if (value.length >= 3) {
      if (check === '') {
        setIsId(true);
      } else {
        setIsMailModalOpen(true);
        setIsId(false);
      }
    } else if (value.length < 1) {
      setIsMailModalOpen(false);
    }
  }, [t]);

  const onChangeCodeInput = useCallback((event:React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setCode(value.toUpperCase());
  }, []);

  /** 이메일 확인 */
  const onCheckEmail = useCallback(async () => {
    const target = id || email;
    const result = await postCheckEmailStatus(target);
    switch (result) {
      case 'email exists with password':
      case 'email exists without password':
        return true;
      case 'email does not exist':
        await alert(t(Message.등록되지않은이메일입니다));
        return false;
      case 'not email format':
        await alert(t(Message.입력한이메일이올바르지않습니다));
        return false;
      default:
        await alert(t(Message.처리도중서버에서오류가발생했습니다잠시후다시시도하시거나관리자에게문의해주세요));
        return false;
    }
  }, [alert, email, id, t]);

  /** 인증코드 확인 이메일 전송 */
  const onClickSendMail = useCallback(async () => {
    setIsLoading(true);
    if (confirmCodeType === 'pwreset') {
      const checkEmail = await onCheckEmail();
      if (!checkEmail) { // 이메일 확인에 실패할 경우 리턴
        setIsLoading(false);
        return false;
      }
    }

    const result = await postSendConfirmEmail(confirmCodeType, id || email);
    if (result === 0) {
      setIsLoading(false);
      await alert(t(Message.이메일전송에실패했습니다잠시후다시시도해주세요));
      stopCountDown();
      return false;
    }
    startCountDown(result);
    setIsMailModalOpen(false); // 전송 안내 모달 사라짐
    setSendComplete(true); // 인증코드 input창이 생김, 이메일 인풋 disabled됨
    setIsTimeModalOpen(true); // 시간 보여주는 모달 열기

    setIsLoading(false);
    return true;
  }, [alert, email, id, startCountDown, stopCountDown, confirmCodeType, onCheckEmail, t]);

  /** 회원가입 */
  const onhandleSignup = useCallback(async () => {
    const res = await postJoinMember(register, register === 'general' ? {
      email, password, name,
    } : {
      email, password, name, phone, belong, actualName,
    });
    const { data, error } = res;

    if (data) {
      dispatch(actions.user.authModal.setToken({ at: res.data.at, rt: res.data.rt }));
      await dispatch(actions.user.authModal.setCurrentPage(EAuthModalPage.COMPLETE));
      return;
    }

    switch (error) {
      case 'email is not valid':
      case 'email already exists':
        await alert(t(Message.올바른이메일이아닙니다));
        break;
      default:
        await alert(t(Message.처리도중서버에서오류가발생했습니다잠시후다시시도하시거나관리자에게문의해주세요));
        break;
    }
  }, [register, email, password, name, phone, belong, actualName, dispatch, alert, t]);

  const onClickSubmitCode = useCallback(async () => {
    setIsLoading(true);
    // 시간 초과한 경우
    if (remainSeconds <= 0) {
      setCode('');
      setSendComplete(false);
      stopCountDown();
      setIsLoading(false);
      await alert(t(Message.인증유효시간이지났습니다다시진행해주세요));
      return;
    }
    // 코드를 입력하지 않은 경우
    if (code.length <= 0) {
      setIsLoading(false);
      await alert(t(Message.인증코드를입력해주세요));
      return;
    }
    const { data, error } = await postCheckConfirmCode({
      type: confirmCodeType,
      code,
      email: id || email,
    });

    if (error) {
      setIsLoading(false);
      await alert(t(error));
      return;
    }
    setIsLoading(false);
    // 에러가 없으면 이메일 인증에 성공
    await alert(t(Message.인증에성공하였습니다));

    if (confirmCodeType === 'email') {
      onhandleSignup();
    } else if (confirmCodeType === 'pwreset') {
      dispatch(actions.user.authModal.setCurrentPage(EAuthModalPage.RESET_PASSWORD));
    } else if (confirmCodeType === 'social') {
      const result = await postNewSocialEmail(id, token.at);
      if (result) {
        if (data?.isAgreed) {
          dispatch(actions.user.authModal.setCurrentPage(EAuthModalPage.COMPLETE));
        } else {
          dispatch(actions.user.authModal.setCurrentPage(EAuthModalPage.ARGREE_POLICY));
        }
      }
    } else {
      await alert(t(Message.처리도중서버에서오류가발생했습니다잠시후다시시도하시거나관리자에게문의해주세요));
    }
  }, [alert, code, confirmCodeType, dispatch, email, id, onhandleSignup, remainSeconds, stopCountDown, t, token.at]);

  const showRemainTime = useMemo(() => {
    if (remainSeconds <= 0) {
      return `${t(Message.남은시간)}: ${t(Message.시간초과)}`;
      setIsTimeModalOpen(false);
    }
    const m = Math.floor(remainSeconds / 60);
    const s = Math.floor(remainSeconds % 60);
    if (i18n.language === 'en') {
      return `${t(Message.남은시간)}: ${m} ${m !== 1 ? 'minutes' : 'minute'} ${s} ${s !== 1 ? 'seconds' : 'second'}`;
    }
    return `${t(Message.남은시간)}: ${m}${t(Message.분)} ${s}${t(Message.초)}`;
  }, [i18n.language, remainSeconds, t]);

  const onClickSendMailAgain = useCallback(async () => {
    const res = await onClickSendMail();
    if (res) {
      await alert(t(Message.이메일을다시전송했습니다이메일보관함을확인해주세요));
    }
  }, [alert, onClickSendMail, t]);

  const handleEmailOnKeyPress = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      if (idMsg === '') {
        onClickSendMail();
      }
    }
  }, [idMsg, onClickSendMail]);

  const handleCodeOnKeyPress = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      if (code.length > 0) {
        onClickSubmitCode();
      }
    }
  }, [code.length, onClickSubmitCode]);

  return (
    <Wrapper>
      <ButtonDiv>
        <BasicButtonLabel>
          {`${t(Message.이미계정이있으신가요)}`}
        </BasicButtonLabel>
        <MobileLogo />
        <BasicButton
          className="password-reset"
          onClick={gotoLogin}
        >
          {t(Message.로그인)}
        </BasicButton>
      </ButtonDiv>
      <ContentDiv>
        { pageType === 'reset' && confirmCodeType === 'pwreset' ? (
          <ResetTitle>
            {t(title)}
            {pageType && <TilteSubText>{t(Message.보다안전한서비스이용을위해비밀번호를재설정해주세요)}</TilteSubText>}
          </ResetTitle>
        ) : <Title>{t(title)}</Title>}

        {(confirmCodeType === 'email' || email !== '')
          ? (
            <>
              <EmailInput disabled value={email} validate />
              {!isTimeModalOpen && <BalloonModal fade={false} text={t(Message.입력한이메일로인증이메일을발송합니다)} />}
            </>
          ) : (
            <EmailInput
              ref={focusRef}
              placeholder={t(Message.가입한이메일주소를입력해주세요)}
              centered
              onChange={onChangeIdInput}
              onKeyPress={handleEmailOnKeyPress}
              disabled={sendComplete}
              validate={id.length === 0 ? true : isId}
            />
          )}

        {isMailModalOpen
        && (isId ? <BalloonModal fade={false} text={t(Message.입력한이메일로인증이메일을발송합니다)} />
          : <BalloonModal fade={false} text={idMsg} />)}

        {!sendComplete
          ? (
            <>
              <ButtonContainer>
                <Button type="button" onClick={confirmCodeType === 'pwreset' ? gotoLogin : goBack}>
                  {t(Message.이전)}
                </Button>
                <EmailButton
                  type="button"
                  disabled={
                    // (confirmCodeType === 'pwreset' ? !isId : false) && !isLoading
                    (confirmCodeType === 'pwreset' ? (!isId && email === '') : false) && !isLoading
                  }
                  isLoading={isLoading}
                  onClick={onClickSendMail}
                >
                  {isLoading ? (
                    <LoadingSpinner />
                  ) : <div>{t(Message.이메일보내기)}</div>}
                </EmailButton>
              </ButtonContainer>
            </>
          )
          : (
            <>
              <CodeInput
                onChange={onChangeCodeInput}
                onKeyPress={handleCodeOnKeyPress}
                placeholder={t(Message.인증코드를입력해주세요)}
                validate={code.length > 0}
                centered
              />
              {isTimeModalOpen && (
                <BalloonModal
                  fade={false}
                  text={`이메일로 전송된 인증코드를 입력하여 인증을 완료해주세요. \n ${showRemainTime}`}
                />
              )}
              <ButtonContainer
                className="check"
              >
                <Button
                  type="button"
                  active
                  onClick={onClickSubmitCode}
                  disabled={code.length <= 0}
                  isLoading={isLoading}
                >
                  {isLoading ? (
                    <LoadingSpinner />
                  ) : t(Message.확인)}
                </Button>
              </ButtonContainer>
              <TextContainer>
                <Text>{t(Message.이메일을받지못했나요)}</Text>
                <TextLink onClick={onClickSendMailAgain}>{t(Message.이메일재전송)}</TextLink>
              </TextContainer>
            </>
          )}

      </ContentDiv>
    </Wrapper>

  );
};
interface ButtonProps {
  active?: boolean;
  isLoading?: boolean;
}

const Button = styled(BasicButton)<ButtonProps>`
  height: 50px;
  flex: 1;
  background-color: ${(props) => (props.active ? theme.color.purple : '#4B586A')};
  display: flex;
  align-items: center;
  justify-content: center;
  :disabled{
    background-color: ${({ isLoading }) => (isLoading ? theme.color.purple : '#F0F0F0')};
    color: ${theme.color.darkGray};
  }
  ${media.lg`
    padding: 17px 0;
    width: 163px;
  `}
  @media(max-width: 380px){
    padding: 17px 0px;
    width: 138px;
  }
`;

const EmailButton = styled(Button)`
  background-color: ${theme.color.purple}; 
`;

const ButtonContainer = styled(Container)`
  padding-top: 80px;
  &.check {
    max-width: 420px;
    width: 100%;
  }
`;

const TextContainer = styled.div`
  padding: 30px;
  display: flex;
  flex-direction: column;
  /* position: fixed; */
  bottom: 10%;
`;

const Text = styled.div`
  font-family: ${theme.fontFamily.regular};
  font-size: 15px;
  line-height: 18px;
  text-align: center;
  padding: 20px;
  color: #5a6089;
`;

const TilteSubText = styled.div`
  font-family: ${theme.fontFamily.regular};
  font-size: 14px;
  line-height: 22px;
  ${media.lg`
    font-family: ${theme.fontFamily.medium};
    /* text-align: center; */
    padding-bottom: 22px;
  `}
`;

const ResetTitle = styled(Title)`
  display: flex;
  flex-direction: column;
  gap: 22px;
  padding-bottom: 12px;
`;
